import { create, muiDialogV5, useModal } from '@ebay/nice-modal-react'
import React, { useState } from 'react'
import { Box, InputAdornment, Typography, styled } from '@mui/material'
import { useAccount, useNetwork } from 'wagmi'
import { useBoolean } from 'ahooks'
import { toast } from 'react-toastify'
import { BigNumber } from 'ethers'
import Check from '../Check'
import StakeButton from './StakeButton'
import Dialog from '@/components/common/Dialog'
import { FlexCol } from '@/components/common/Columns'
import NumberInput from '@/components/common/NumberInput'
import { MaxButton } from '@/components/common/MaxButton'
import { useBalanceOf } from '@/hooks/brc20Hooks'
import { getBrc20ContractAddress } from '@/utils/contract'
import { SupportedTicker, TICKERS_MAP_BY_NAME } from '@/constants/token'
import { formatNumber, getTokenDecimals } from '@/utils'
import TokenLogo from '@/components/common/TokenLogo'

const Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px 4px;

  background: rgba(191, 255, 11, 0.1);
  border-radius: 4px;

  margin-right: 4px;

  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  font-feature-settings: 'ss01' on, 'case' on, 'calt' off;
`

export const MinimumAmountWrapper = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: 8px;

  margin-top: 10px;
  align-self: flex-end;
`
export const Text3 = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`

const StakeDialog = ({
  tickerName,
  poolId,
  onSuccess,
  lockTime,
}: {
  tickerName: SupportedTicker
  poolId: number
  onSuccess: () => void
  lockTime: number
}) => {
  const modal = useModal()

  const [amountInput, setAmountInput] = useState('')
  const [isChecked, { toggle: toggleChecked }] = useBoolean()

  const { address: account } = useAccount()
  const { chain } = useNetwork()

  const { data: brc20Balance, loading: isBrc20BalanceLoading } = useBalanceOf({
    address: account,
    token: getBrc20ContractAddress(tickerName, chain?.id),
  })

  const decimal = getTokenDecimals(tickerName, chain?.id)

  return (
    <Dialog {...muiDialogV5(modal)}>
      <FlexCol sx={{ rowGap: { xs: 24, md: 40 }, minWidth: { xs: 'auto', md: 400 } }}>
        <Typography fontSize={{ xs: 24, md: 32 }}>Stake {tickerName}</Typography>

        <FlexCol sx={{ rowGap: { xs: 12, md: 20 } }}>
          <MinimumAmountWrapper>
            <Text3 sx={{ opacity: 0.5 }}>Available</Text3>
            <Text3>
              {brc20Balance && chain?.id && !isBrc20BalanceLoading
                ? formatNumber(brc20Balance.toString(), decimal)
                : '-'}{' '}
              {TICKERS_MAP_BY_NAME?.[tickerName].erc20Tokens?.[chain?.id]?.symbol}
            </Text3>
            <TokenLogo
              src={
                TICKERS_MAP_BY_NAME?.[tickerName].logoSrcSet?.erc || TICKERS_MAP_BY_NAME?.[tickerName].logoSrcSet.brc
              }
              size={20}
              alt={tickerName}
            />
            <Box sx={{ border: '1px solid #BFFF0B33', px: 8 }}>
              <Text3 sx={{ color: '#BFFF0B' }}>ERC20</Text3>
            </Box>
          </MinimumAmountWrapper>

          <NumberInput
            value={amountInput}
            onUserInput={(value) => {
              if (value.split('.')?.[1] && value.split('.')?.[1]?.length > 6) {
                return
              } else {
                setAmountInput(value)
              }
            }}
            startAdornment={
              <InputAdornment position="start">
                <Tag>Amount</Tag>
              </InputAdornment>
            }
            endAdornment={
              <MaxButton
                onClick={() => {
                  if (brc20Balance && chain?.id) {
                    setAmountInput(formatNumber(brc20Balance.toString(), decimal, { shouldCommify: false }))
                  }
                }}
              />
            }
          />

          <Check value={isChecked} toggle={toggleChecked} lockTime={lockTime} action="stake" />

          <StakeButton
            amount={amountInput}
            decimal={decimal}
            poolId={poolId}
            disabled={!isChecked}
            tokenAddress={TICKERS_MAP_BY_NAME[tickerName].erc20Tokens[chain?.id].address}
            brc20Balance={brc20Balance}
            onSuccess={() => {
              toast.success('Staked successfully')
              modal.hide()
              onSuccess()
            }}
          />
        </FlexCol>
      </FlexCol>
    </Dialog>
  )
}

export default create(StakeDialog)
